import { useState, useEffect, useRef, useCallback } from "react";
import AnimationService from "../../../services/AnimationService";
import {
  AnimationErrorMessages,
  getUserFacingMessage,
  logAnimationError,
  logAnimationWarning,
} from "../errors/animationErrors";
import {
  ANIMATION_CLIENT_MAX_WAIT_MS,
  ANIMATION_POLL_INTERVAL_MS,
  getPollResultFromStatusResponse,
  isClientTimeoutExceeded,
  runAnimationPollingStep,
} from "../polling/pollStatus";
import {
  extractErrorMessage,
  isFailureStatus,
  isSuccessStatus,
  normalizeStatus,
  resolveVideoIdFromStatus,
} from "../utils";

const STATUS_UTILS = {
  normalizeStatus,
  isSuccessStatus,
  isFailureStatus,
  extractErrorMessage,
};

const useAnimationPolling = (
  animationGuid,
  userData,
  setStatusData,
  setPreviewData,
  setVideoUrl,
  setIsLoading,
  setErrorMessage,
  onPreparingPlaybackChange
) => {
  const [hasError, setHasError] = useState(false);
  const pollingRef = useRef(null);
  const startTimeRef = useRef(null);
  const lastGuidRef = useRef(null);

  const handleError = useCallback(
    (error) => {
      logAnimationError("polling", error);
      setErrorMessage(
        getUserFacingMessage(error, AnimationErrorMessages.POLLING_FAILED)
      );
      setIsLoading(false);
      setHasError(true);
    },
    [setErrorMessage, setIsLoading]
  );

  const checkClientTimeout = useCallback(() => {
    if (
      isClientTimeoutExceeded(
        startTimeRef.current,
        Date.now(),
        ANIMATION_CLIENT_MAX_WAIT_MS
      )
    ) {
      handleError(new Error(AnimationErrorMessages.TIMEOUT));
      return true;
    }
    return false;
  }, [handleError]);

  const fetchPreviewData = useCallback(
    async (videoId) => {
      try {
        const preview = await AnimationService.viewPreview(
          videoId,
          userData.jwtToken,
          userData
        );
        setPreviewData(preview);
      } catch (previewError) {
        logAnimationWarning("preview", previewError);
      }
    },
    [userData, setPreviewData]
  );

  const fetchVideoStream = useCallback(
    async (videoId) => {
      const maxAttempts = 3;
      const delayMs = 2000;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const streamResponse = await AnimationService.streamVideo(
            videoId,
            userData.jwtToken,
            userData
          );
          const blob = await streamResponse.blob();
          const blobUrl = URL.createObjectURL(blob);
          setVideoUrl(blobUrl);
          return;
        } catch (streamError) {
          logAnimationWarning(`stream attempt ${attempt}/${maxAttempts}`, streamError);
          if (attempt === maxAttempts) {
            setErrorMessage("Animation generated successfully, but the preview is still loading. You can also view it in the Animation Gallery.");
          } else {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
          }
        }
      }
    },
    [userData, setVideoUrl, setErrorMessage]
  );

  const fetchPollResult = useCallback(async () => {
    const statusResponse = await AnimationService.checkStatus(
      animationGuid,
      userData.jwtToken,
      userData
    );
    setStatusData(statusResponse);
    return getPollResultFromStatusResponse(
      statusResponse,
      STATUS_UTILS
    );
  }, [animationGuid, userData, setStatusData]);

  const processCompletedAnimation = useCallback(
    async (statusResponse) => {
      const videoId = resolveVideoIdFromStatus(statusResponse, animationGuid);
      onPreparingPlaybackChange?.(true);
      try {
        await Promise.all([
          fetchPreviewData(videoId),
          fetchVideoStream(videoId),
        ]);
      } finally {
        onPreparingPlaybackChange?.(false);
        setIsLoading(false);
      }
    },
    [
      animationGuid,
      fetchPreviewData,
      fetchVideoStream,
      setIsLoading,
      onPreparingPlaybackChange,
    ]
  );

  useEffect(() => {
    if (animationGuid && animationGuid !== lastGuidRef.current) {
      setHasError(false);
    }
  }, [animationGuid]);

  useEffect(() => {
    if (!animationGuid || !userData || hasError) {
      return;
    }

    if (lastGuidRef.current !== animationGuid) {
      startTimeRef.current = Date.now();
      lastGuidRef.current = animationGuid;
    }

    const poll = async () => {
      const step = await runAnimationPollingStep({
        checkClientTimeout,
        fetchPollResult,
        onJobCompleted: processCompletedAnimation,
        onFailure: handleError,
      });
      if (step.type === "continue") {
        pollingRef.current = setTimeout(
          poll,
          ANIMATION_POLL_INTERVAL_MS
        );
      }
    };

    poll();

    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
    };
  }, [
    animationGuid,
    userData,
    hasError,
    checkClientTimeout,
    fetchPollResult,
    processCompletedAnimation,
    handleError,
  ]);

  return null;
};

export default useAnimationPolling;
